import { useId, type SVGProps } from 'react'

import {
  clamp,
  compositeHexColor,
  contrastRatio,
  hexStringToHsl,
  hslToRgb,
  hueDistance,
  mixHexColors,
  normalizeHue,
  parseHexColor,
  rgbToHexString,
  type HslColor,
  type RgbColor,
} from '@/utils/color'

export type HueboVoxelLogoMockPoint = readonly [number, number]

type Point = HueboVoxelLogoMockPoint
type FacePath = readonly Point[]

export type HueboVoxelLogoMockAttachmentEdge = {
  back: Point
  front: Point
}

type AttachmentEdge = HueboVoxelLogoMockAttachmentEdge

type FaceColors = {
  top: string
  left: string
  right: string
  seam: string
  topSeam: string
  leftSeam: string
  rightSeam: string
}

export type HueboVoxelLogoMockBaseColors = {
  columns: string
  bridge: string
}

export type HueboVoxelLogoMockRenderMode = 'solid' | 'line-art'

export type HueboVoxelLogoMockPaletteOptions = {
  lineArtFill?: string
}

export type HueboVoxelLogoGeneratedColorOption = {
  id: string
  name: string
  colors: HueboVoxelLogoMockBaseColors
  contrast: {
    columns: number
    bridge: number
  }
}

export type HueboVoxelLogoMockPalette = {
  tile: string
  tileStroke: string
  column: FaceColors
  columnAlternate: FaceColors
  bridge: FaceColors
  bridgeAccent: FaceColors
  bridgeEnd: FaceColors
}

export type HueboVoxelLogoMockGeometry = {
  viewBox: number
  tileInset: number
  tileRadius: number
  cubeSize: number
  columnCubes: number
  bridgeCubes: number
  columnSpanCubes: number
  leftCenterX: number
  topY: number
  bridgeRow: number
  bridgeRightRowOffset: number
}

export type ResolvedHueboVoxelLogoMockGeometry = HueboVoxelLogoMockGeometry & {
  unitX: number
  unitY: number
  rightCenterX: number
}

export type HueboVoxelLogoMockProps = Omit<SVGProps<SVGSVGElement>, 'color'> & {
  baseColors?: Partial<HueboVoxelLogoMockBaseColors>
  geometry?: Partial<HueboVoxelLogoMockGeometry>
  renderMode?: HueboVoxelLogoMockRenderMode
  strokeWidth?: number
  lineArtFill?: string
  showTile?: boolean
  title?: string
}

type GeneratedColorScheme = {
  id: string
  name: string
  columnHue: (background: HslColor) => number
  bridgeHue: (background: HslColor) => number
}

type HslAdjustment = {
  h: number
  s: number
  l: number
}

const ISO_ANGLE_RADIANS = Math.PI / 6
const LOGO_STROKE_LINE_JOIN = 'round'

const GENERATED_COLOR_SCHEMES: readonly GeneratedColorScheme[] = [
  {
    id: 'cyan-amber',
    name: 'Cyan / amber',
    columnHue: () => 184,
    bridgeHue: () => 38,
  },
  {
    id: 'sky-coral',
    name: 'Sky / coral',
    columnHue: () => 204,
    bridgeHue: () => 18,
  },
  {
    id: 'mint-rose',
    name: 'Mint / rose',
    columnHue: () => 156,
    bridgeHue: () => 336,
  },
  {
    id: 'violet-gold',
    name: 'Violet / gold',
    columnHue: () => 266,
    bridgeHue: () => 48,
  },
  {
    id: 'blue-lime',
    name: 'Blue / lime',
    columnHue: () => 220,
    bridgeHue: () => 82,
  },
  {
    id: 'background-triad',
    name: 'Background triad',
    columnHue: (background) => normalizeHue(background.h + 132),
    bridgeHue: (background) => normalizeHue(background.h + 252),
  },
  {
    id: 'background-split',
    name: 'Split contrast',
    columnHue: (background) => normalizeHue(background.h + 165),
    bridgeHue: (background) => normalizeHue(background.h + 35),
  },
  {
    id: 'aqua-ruby',
    name: 'Aqua / ruby',
    columnHue: () => 176,
    bridgeHue: () => 350,
  },
]

export const HUEBO_LOGO_MOCK_BASE_COLORS: HueboVoxelLogoMockBaseColors = {
  columns: '#38d8cb',
  bridge: '#ffb739',
}

export const HUEBO_LOGO_MOCK_DEFAULT_STROKE_WIDTH = 0.82

export const HUEBO_LOGO_MOCK_DEFAULT_LINE_ART_STROKE_WIDTH = 2.5

export const HUEBO_LOGO_MOCK_DEFAULT_LINE_ART_FILL = '#ffffff'

export const HUEBO_LOGO_MOCK_DEFAULT_GEOMETRY: HueboVoxelLogoMockGeometry = {
  viewBox: 128,
  tileInset: 7,
  tileRadius: 21,
  cubeSize: 14.75,
  columnCubes: 6,
  bridgeCubes: 3,
  columnSpanCubes: 3.5,
  leftCenterX: 34.5,
  topY: 17.5,
  bridgeRow: 2,
  bridgeRightRowOffset: 1,
}

export function generateHueboVoxelLogoBaseColorsFromBackground(background: string): HueboVoxelLogoMockBaseColors {
  return generateHueboVoxelLogoColorOptionsFromBackground(background, 1)[0]?.colors ?? HUEBO_LOGO_MOCK_BASE_COLORS
}

export function defaultHueboVoxelLogoMockStrokeWidth(renderMode: HueboVoxelLogoMockRenderMode): number {
  return renderMode === 'line-art'
    ? HUEBO_LOGO_MOCK_DEFAULT_LINE_ART_STROKE_WIDTH
    : HUEBO_LOGO_MOCK_DEFAULT_STROKE_WIDTH
}

export function lineArtStrokeColorForBackground(background: string): string {
  const backgroundHsl = hexStringToHsl(background)
  const strokeHue = normalizeHue(backgroundHsl.h + 180)
  const strokeSaturation = clamp(backgroundHsl.s + 42, 48, 88)
  const strokeLightness = backgroundHsl.l < 52 ? 72 : 28

  return rgbToHexString(hslToRgb(strokeHue, strokeSaturation, strokeLightness))
}

export function generateHueboVoxelLogoColorOptionsFromBackground(
  background: string,
  limit = 5,
): HueboVoxelLogoGeneratedColorOption[] {
  const backgroundRgb = parseHexColor(background)
  const backgroundHsl = hexStringToHsl(background)
  const seen = new Set<string>()

  return GENERATED_COLOR_SCHEMES
    .map((scheme) => {
      const columnHue = normalizeHue(scheme.columnHue(backgroundHsl))
      const bridgeHue = normalizeHue(scheme.bridgeHue(backgroundHsl))
      const columns = bestLogoBaseColorForBackground(columnHue, backgroundRgb, backgroundHsl)
      const bridge = bestLogoBaseColorForBackground(bridgeHue, backgroundRgb, backgroundHsl)
      const columnsContrast = contrastRatio(parseHexColor(columns), backgroundRgb)
      const bridgeContrast = contrastRatio(parseHexColor(bridge), backgroundRgb)
      const hueSeparation = hueDistance(columnHue, bridgeHue) / 180
      const backgroundSeparation = Math.min(hueDistance(columnHue, backgroundHsl.h), hueDistance(bridgeHue, backgroundHsl.h)) / 180
      const score = Math.min(columnsContrast, bridgeContrast) * 2.5
        + (columnsContrast + bridgeContrast) * 0.3
        + hueSeparation * 1.8
        + backgroundSeparation * 0.9

      return {
        id: scheme.id,
        name: scheme.name,
        colors: { columns, bridge },
        contrast: {
          columns: columnsContrast,
          bridge: bridgeContrast,
        },
        score,
      }
    })
    .sort((a, b) => b.score - a.score)
    .filter((option) => {
      const key = `${option.colors.columns}:${option.colors.bridge}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .slice(0, limit)
    .map(({ id, name, colors, contrast }) => ({ id, name, colors, contrast }))
}

export function buildHueboVoxelLogoMockPalette(
  baseColors: Partial<HueboVoxelLogoMockBaseColors> = {},
  renderMode: HueboVoxelLogoMockRenderMode = 'solid',
  options: HueboVoxelLogoMockPaletteOptions = {},
): HueboVoxelLogoMockPalette {
  if (renderMode === 'line-art') return buildHueboVoxelLogoMockLineArtPalette(baseColors, options.lineArtFill)

  const columns = baseColors.columns ?? HUEBO_LOGO_MOCK_BASE_COLORS.columns
  const bridge = baseColors.bridge ?? HUEBO_LOGO_MOCK_BASE_COLORS.bridge
  const alternateColumn = adjustHsl(columns, { h: -3, s: -0.04, l: -0.02 })
  const columnFaces = faceFromBase(columns)
  const bridgeFaces = faceFromBase(bridge)

  return {
    tile: '#172327',
    tileStroke: '#31484d',
    column: columnFaces,
    columnAlternate: faceFromBase(alternateColumn, '#061b1e'),
    bridge: bridgeFaces,
    bridgeAccent: bridgeFaces,
    bridgeEnd: bridgeFaces,
  }
}

export function HueboVoxelLogoMock({
  baseColors,
  geometry: geometryOverrides,
  renderMode = 'solid',
  strokeWidth,
  lineArtFill = HUEBO_LOGO_MOCK_DEFAULT_LINE_ART_FILL,
  showTile = true,
  title,
  ...svgProps
}: HueboVoxelLogoMockProps) {
  const titleId = useId()
  const shadowId = useId()
  const transparentLineMaskId = useId()
  const geometry = resolveHueboVoxelLogoMockGeometry(geometryOverrides)
  const resolvedStrokeWidth = strokeWidth ?? defaultHueboVoxelLogoMockStrokeWidth(renderMode)
  const palette = buildHueboVoxelLogoMockPalette(baseColors, renderMode, { lineArtFill })
  const maskTransparentLineArt = renderMode === 'line-art' && isTransparentPaint(lineArtFill)
  const tileSize = geometry.viewBox - geometry.tileInset * 2

  return (
    <svg
      viewBox={`0 0 ${geometry.viewBox} ${geometry.viewBox}`}
      role={title ? 'img' : undefined}
      aria-labelledby={title ? titleId : undefined}
      aria-hidden={title ? undefined : true}
      {...svgProps}
    >
      {title && <title id={titleId}>{title}</title>}
      <defs>
        <filter id={shadowId} x="-25%" y="-20%" width="150%" height="155%">
          <feDropShadow dx="0" dy="7" stdDeviation="7" floodColor="#071013" floodOpacity="0.32" />
        </filter>
        {maskTransparentLineArt && (
          <TransparentLineArtMask
            id={transparentLineMaskId}
            geometry={geometry}
            strokeWidth={resolvedStrokeWidth}
          />
        )}
      </defs>

      {showTile && (
        <g filter={`url(#${shadowId})`}>
          <rect
            x={geometry.tileInset}
            y={geometry.tileInset}
            width={tileSize}
            height={tileSize}
            rx={geometry.tileRadius}
            fill={palette.tile}
            stroke={palette.tileStroke}
          />
        </g>
      )}

      <g>
        <g mask={maskTransparentLineArt ? `url(#${transparentLineMaskId})` : undefined}>
          <IsoPillar x={geometry.leftCenterX} geometry={geometry} colors={palette.column} strokeWidth={resolvedStrokeWidth} />
          <IsoPillar x={geometry.rightCenterX} geometry={geometry} colors={palette.columnAlternate} strokeWidth={resolvedStrokeWidth} />
        </g>
        <IsoBridge geometry={geometry} palette={palette} strokeWidth={resolvedStrokeWidth} />
      </g>
    </svg>
  )
}

export function buildHueboVoxelLogoMockSvg(
  baseColors: Partial<HueboVoxelLogoMockBaseColors> = {},
  options: {
    geometry?: Partial<HueboVoxelLogoMockGeometry>
    renderMode?: HueboVoxelLogoMockRenderMode
    strokeWidth?: number
    lineArtFill?: string
    showTile?: boolean
    title?: string
  } = {},
): string {
  const renderMode = options.renderMode ?? 'solid'
  const geometry = resolveHueboVoxelLogoMockGeometry(options.geometry)
  const strokeWidth = options.strokeWidth ?? defaultHueboVoxelLogoMockStrokeWidth(renderMode)
  const palette = buildHueboVoxelLogoMockPalette(baseColors, renderMode, { lineArtFill: options.lineArtFill })
  const maskTransparentLineArt = renderMode === 'line-art' && isTransparentPaint(options.lineArtFill)
  const tileSize = geometry.viewBox - geometry.tileInset * 2
  const title = options.title ? `<title>${escapeXml(options.title)}</title>` : ''
  const transparentLineMaskId = 'huebo-logo-transparent-line-mask'
  const defs = maskTransparentLineArt
    ? `<defs>${transparentLineArtMaskMarkup(transparentLineMaskId, geometry, strokeWidth)}</defs>`
    : ''
  const tile = options.showTile ?? true
    ? `<rect x="${geometry.tileInset}" y="${geometry.tileInset}" width="${tileSize}" height="${tileSize}" rx="${geometry.tileRadius}" fill="${palette.tile}" stroke="${palette.tileStroke}" />`
    : ''
  const pillars = [
    isoPillarMarkup(geometry.leftCenterX, geometry, palette.column, strokeWidth),
    isoPillarMarkup(geometry.rightCenterX, geometry, palette.columnAlternate, strokeWidth),
  ].join('')
  const maskedPillars = maskTransparentLineArt ? `<g mask="url(#${transparentLineMaskId})">${pillars}</g>` : pillars
  const cubes = `${maskedPillars}${isoBridgeMarkup(geometry, palette, strokeWidth)}`

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${geometry.viewBox} ${geometry.viewBox}">${title}${defs}${tile}<g>${cubes}</g></svg>`
}

function TransparentLineArtMask({
  id,
  geometry,
  strokeWidth,
}: {
  id: string
  geometry: ResolvedHueboVoxelLogoMockGeometry
  strokeWidth: number
}) {
  return (
    <mask id={id} maskUnits="userSpaceOnUse" x="0" y="0" width={geometry.viewBox} height={geometry.viewBox}>
      <rect x="0" y="0" width={geometry.viewBox} height={geometry.viewBox} fill="white" />
      <BridgeOccluder geometry={geometry} strokeWidth={strokeWidth} />
    </mask>
  )
}

function BridgeOccluder({
  geometry,
  strokeWidth,
}: {
  geometry: ResolvedHueboVoxelLogoMockGeometry
  strokeWidth: number
}) {
  const { topSegments, frontSegments, rightCap } = bridgeFacePaths(geometry)

  return (
    <g
      fill="black"
      stroke="black"
      strokeWidth={strokeWidth}
      strokeLinejoin={LOGO_STROKE_LINE_JOIN}
    >
      {frontSegments.map((segment, index) => (
        <path key={`front-${index}`} d={pathFromPoints(segment)} />
      ))}
      <path d={pathFromPoints(rightCap)} />
      {topSegments.map((segment, index) => (
        <path key={`top-${index}`} d={pathFromPoints(segment)} />
      ))}
    </g>
  )
}

function IsoPillar({
  x,
  geometry,
  colors,
  strokeWidth,
}: {
  x: number
  geometry: ResolvedHueboVoxelLogoMockGeometry
  colors: FaceColors
  strokeWidth: number
}) {
  const { topY, unitX, unitY, cubeSize, columnCubes } = geometry
  const segments = Array.from({ length: columnCubes }, (_, index) => {
    const topOffset = index * cubeSize
    const bottomOffset = (index + 1) * cubeSize
    const left = [
      [x - unitX, topY + unitY + topOffset],
      [x, topY + unitY * 2 + topOffset],
      [x, topY + unitY * 2 + bottomOffset],
      [x - unitX, topY + unitY + bottomOffset],
    ] as const
    const right = [
      [x + unitX, topY + unitY + topOffset],
      [x, topY + unitY * 2 + topOffset],
      [x, topY + unitY * 2 + bottomOffset],
      [x + unitX, topY + unitY + bottomOffset],
    ] as const

    return { left, right }
  })
  const top = [
    [x, topY],
    [x + unitX, topY + unitY],
    [x, topY + unitY * 2],
    [x - unitX, topY + unitY],
  ] as const

  return (
    <g strokeWidth={strokeWidth} strokeLinejoin={LOGO_STROKE_LINE_JOIN}>
      {segments.map((segment, index) => (
        <g key={index}>
          <path d={pathFromPoints(segment.left)} fill={colors.left} stroke={colors.leftSeam} />
          <path d={pathFromPoints(segment.right)} fill={colors.right} stroke={colors.rightSeam} />
        </g>
      ))}
      <path d={pathFromPoints(top)} fill={colors.top} stroke={colors.topSeam} />
    </g>
  )
}

function isoPillarMarkup(
  x: number,
  geometry: ResolvedHueboVoxelLogoMockGeometry,
  colors: FaceColors,
  strokeWidth: number,
): string {
  const { topY, unitX, unitY, cubeSize, columnCubes } = geometry
  const segments = Array.from({ length: columnCubes }, (_, index) => {
    const topOffset = index * cubeSize
    const bottomOffset = (index + 1) * cubeSize
    const left = [
      [x - unitX, topY + unitY + topOffset],
      [x, topY + unitY * 2 + topOffset],
      [x, topY + unitY * 2 + bottomOffset],
      [x - unitX, topY + unitY + bottomOffset],
    ] as const
    const right = [
      [x + unitX, topY + unitY + topOffset],
      [x, topY + unitY * 2 + topOffset],
      [x, topY + unitY * 2 + bottomOffset],
      [x + unitX, topY + unitY + bottomOffset],
    ] as const

    return `<path d="${pathFromPoints(left)}" fill="${colors.left}" stroke="${colors.leftSeam}" /><path d="${pathFromPoints(right)}" fill="${colors.right}" stroke="${colors.rightSeam}" />`
  }).join('')
  const top = [
    [x, topY],
    [x + unitX, topY + unitY],
    [x, topY + unitY * 2],
    [x - unitX, topY + unitY],
  ] as const

  return `<g stroke-width="${formatNumber(strokeWidth)}" stroke-linejoin="${LOGO_STROKE_LINE_JOIN}">${segments}<path d="${pathFromPoints(top)}" fill="${colors.top}" stroke="${colors.topSeam}" /></g>`
}

function IsoBridge({
  geometry,
  palette,
  strokeWidth,
}: {
  geometry: ResolvedHueboVoxelLogoMockGeometry
  palette: HueboVoxelLogoMockPalette
  strokeWidth: number
}) {
  const { topSegments, frontSegments, rightCap } = bridgeFacePaths(geometry)

  return (
    <g strokeWidth={strokeWidth} strokeLinejoin={LOGO_STROKE_LINE_JOIN}>
      {frontSegments.map((segment, index) => {
        const colors = bridgeFace(index, geometry.bridgeCubes, palette)
        return <path key={index} d={pathFromPoints(segment)} fill={colors.left} stroke={colors.leftSeam} />
      })}
      <path d={pathFromPoints(rightCap)} fill={palette.bridgeEnd.right} stroke={palette.bridgeEnd.rightSeam} />
      {topSegments.map((segment, index) => {
        const colors = bridgeFace(index, geometry.bridgeCubes, palette)
        return <path key={index} d={pathFromPoints(segment)} fill={colors.top} stroke={colors.topSeam} />
      })}
    </g>
  )
}

function isoBridgeMarkup(
  geometry: ResolvedHueboVoxelLogoMockGeometry,
  palette: HueboVoxelLogoMockPalette,
  strokeWidth: number,
): string {
  const { topSegments, frontSegments, rightCap } = bridgeFacePaths(geometry)
  const topMarkup = topSegments.map((points, index) => {
    const colors = bridgeFace(index, geometry.bridgeCubes, palette)
    return `<path d="${pathFromPoints(points)}" fill="${colors.top}" stroke="${colors.topSeam}" />`
  }).join('')
  const frontMarkup = frontSegments.map((points, index) => {
    const colors = bridgeFace(index, geometry.bridgeCubes, palette)
    return `<path d="${pathFromPoints(points)}" fill="${colors.left}" stroke="${colors.leftSeam}" />`
  }).join('')

  return `<g stroke-width="${formatNumber(strokeWidth)}" stroke-linejoin="${LOGO_STROKE_LINE_JOIN}">${frontMarkup}<path d="${pathFromPoints(rightCap)}" fill="${palette.bridgeEnd.right}" stroke="${palette.bridgeEnd.rightSeam}" />${topMarkup}</g>`
}

function bridgeFacePaths(
  geometry: ResolvedHueboVoxelLogoMockGeometry,
): {
  topSegments: FacePath[]
  frontSegments: FacePath[]
  rightCap: FacePath
} {
  const invariants = getHueboVoxelLogoMockInvariantEdges(geometry)
  const topSegments = Array.from({ length: geometry.bridgeCubes }, (_, index) => {
    const start = index / geometry.bridgeCubes
    const end = (index + 1) / geometry.bridgeCubes
    return [
      lerpPoint(invariants.leftTop.back, invariants.rightTop.back, start),
      lerpPoint(invariants.leftTop.back, invariants.rightTop.back, end),
      lerpPoint(invariants.leftTop.front, invariants.rightTop.front, end),
      lerpPoint(invariants.leftTop.front, invariants.rightTop.front, start),
    ] as const
  })
  const frontSegments = Array.from({ length: geometry.bridgeCubes }, (_, index) => {
    const start = index / geometry.bridgeCubes
    const end = (index + 1) / geometry.bridgeCubes
    return [
      lerpPoint(invariants.leftTop.front, invariants.rightTop.front, start),
      lerpPoint(invariants.leftTop.front, invariants.rightTop.front, end),
      lerpPoint(invariants.leftBottom.front, invariants.rightBottom.front, end),
      lerpPoint(invariants.leftBottom.front, invariants.rightBottom.front, start),
    ] as const
  })
  const rightCap = [
    invariants.rightTop.back,
    invariants.rightTop.front,
    invariants.rightBottom.front,
    invariants.rightBottom.back,
  ] as const

  return { topSegments, frontSegments, rightCap }
}

function transparentLineArtMaskMarkup(
  id: string,
  geometry: ResolvedHueboVoxelLogoMockGeometry,
  strokeWidth: number,
): string {
  const { topSegments, frontSegments, rightCap } = bridgeFacePaths(geometry)
  const paths = [
    ...frontSegments,
    rightCap,
    ...topSegments,
  ].map((points) => `<path d="${pathFromPoints(points)}" />`).join('')

  return `<mask id="${id}" maskUnits="userSpaceOnUse" x="0" y="0" width="${geometry.viewBox}" height="${geometry.viewBox}"><rect x="0" y="0" width="${geometry.viewBox}" height="${geometry.viewBox}" fill="white" /><g fill="black" stroke="black" stroke-width="${formatNumber(strokeWidth)}" stroke-linejoin="${LOGO_STROKE_LINE_JOIN}">${paths}</g></mask>`
}

function isTransparentPaint(value: string | undefined): boolean {
  return value?.trim().toLowerCase() === 'transparent'
}

function bridgeFace(index: number, segmentCount: number, palette: HueboVoxelLogoMockPalette): FaceColors {
  if (index === segmentCount - 1) return palette.bridgeEnd
  if (index === 1) return palette.bridgeAccent
  return palette.bridge
}

function faceFromBase(base: string, shadow = '#062022'): FaceColors {
  const top = mixHexColors(base, '#ffffff', 0.34)
  const right = mixHexColors(base, shadow, 0.34)
  const seamSource = mixHexColors(base, '#ffffff', 0.48)
  const leftSeam = compositeHexColor(seamSource, 0.28, base)

  return {
    top,
    left: base,
    right,
    seam: leftSeam,
    topSeam: compositeHexColor(seamSource, 0.28, top),
    leftSeam,
    rightSeam: compositeHexColor(seamSource, 0.28, right),
  }
}

function lineArtFace(fill: string, stroke: string): FaceColors {
  return {
    top: fill,
    left: fill,
    right: fill,
    seam: stroke,
    topSeam: stroke,
    leftSeam: stroke,
    rightSeam: stroke,
  }
}

function buildHueboVoxelLogoMockLineArtPalette(
  baseColors: Partial<HueboVoxelLogoMockBaseColors> = {},
  lineArtFill = HUEBO_LOGO_MOCK_DEFAULT_LINE_ART_FILL,
): HueboVoxelLogoMockPalette {
  const ink = baseColors.columns ?? baseColors.bridge ?? '#161616'
  const paper = lineArtFill
  const lineFace = lineArtFace(paper, ink)

  return {
    tile: '#faf9f4',
    tileStroke: ink,
    column: lineFace,
    columnAlternate: lineFace,
    bridge: lineFace,
    bridgeAccent: lineFace,
    bridgeEnd: lineFace,
  }
}

export function resolveHueboVoxelLogoMockGeometry(
  overrides: Partial<HueboVoxelLogoMockGeometry> = {},
): ResolvedHueboVoxelLogoMockGeometry {
  const geometry = { ...HUEBO_LOGO_MOCK_DEFAULT_GEOMETRY, ...overrides }
  const unitX = Math.cos(ISO_ANGLE_RADIANS) * geometry.cubeSize
  const unitY = Math.sin(ISO_ANGLE_RADIANS) * geometry.cubeSize

  return {
    ...geometry,
    unitX,
    unitY,
    rightCenterX: geometry.leftCenterX + geometry.columnSpanCubes * geometry.cubeSize,
  }
}

export function getHueboVoxelLogoMockInvariantEdges(
  geometry: ResolvedHueboVoxelLogoMockGeometry,
): {
  leftTop: AttachmentEdge
  leftBottom: AttachmentEdge
  rightTop: AttachmentEdge
  rightBottom: AttachmentEdge
} {
  return {
    leftTop: pillarRightAttachmentEdge(geometry.leftCenterX, geometry.bridgeRow, geometry),
    leftBottom: pillarRightAttachmentEdge(geometry.leftCenterX, geometry.bridgeRow + 1, geometry),
    rightTop: bridgeRightVisibleEndEdge(geometry.bridgeRow + geometry.bridgeRightRowOffset, geometry),
    rightBottom: bridgeRightVisibleEndEdge(geometry.bridgeRow + geometry.bridgeRightRowOffset + 1, geometry),
  }
}

function bridgeRightVisibleEndEdge(
  row: number,
  geometry: ResolvedHueboVoxelLogoMockGeometry,
): AttachmentEdge {
  const rowY = geometry.topY + row * geometry.cubeSize
  const back = [geometry.rightCenterX, rowY + geometry.unitY * 2] as const

  return {
    back,
    front: [back[0] - geometry.unitX, back[1] + geometry.unitY],
  }
}

function pillarRightAttachmentEdge(
  centerX: number,
  row: number,
  geometry: ResolvedHueboVoxelLogoMockGeometry,
): AttachmentEdge {
  const rowY = geometry.topY + row * geometry.cubeSize
  return {
    back: [centerX + geometry.unitX, rowY + geometry.unitY],
    front: [centerX, rowY + geometry.unitY * 2],
  }
}

function pathFromPoints(points: readonly Point[]): string {
  return `${points.map(([x, y], index) => `${index === 0 ? 'M' : 'L'} ${formatNumber(x)} ${formatNumber(y)}`).join(' ')} Z`
}

function lerpPoint(from: Point, to: Point, amount: number): Point {
  return [
    from[0] + (to[0] - from[0]) * amount,
    from[1] + (to[1] - from[1]) * amount,
  ]
}

function adjustHsl(hex: string, adjustment: Partial<HslAdjustment>): string {
  const hsl = hexStringToHsl(hex)
  return rgbToHexString(hslToRgb(
    normalizeHue(hsl.h + (adjustment.h ?? 0)),
    clamp(hsl.s + (adjustment.s ?? 0) * 100, 0, 100),
    clamp(hsl.l + (adjustment.l ?? 0) * 100, 0, 100),
  ))
}

function bestLogoBaseColorForBackground(hue: number, backgroundRgb: RgbColor, backgroundHsl: HslColor): string {
  const darkBackground = backgroundHsl.l < 52
  const saturationCandidates = darkBackground
    ? [72, 82, 64, 90]
    : [64, 74, 54, 82]
  const lightnessCandidates = darkBackground
    ? [62, 56, 68, 50, 74]
    : [34, 28, 40, 22, 46]
  let best = rgbToHexString(hslToRgb(normalizeHue(hue), saturationCandidates[0], lightnessCandidates[0]))
  let bestScore = Number.NEGATIVE_INFINITY

  for (const saturation of saturationCandidates) {
    for (const lightness of lightnessCandidates) {
      const rgb = hslToRgb(normalizeHue(hue), saturation, lightness)
      const contrast = contrastRatio(rgb, backgroundRgb)
      const targetLightness = darkBackground ? 62 : 34
      const vividness = saturation / 100 * 0.7 - Math.abs(lightness - targetLightness) / 100 * 0.9
      const score = contrast * 1.8 + vividness

      if (score > bestScore) {
        bestScore = score
        best = rgbToHexString(rgb)
      }
    }
  }

  return best
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2)
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
